import type { ReactNode } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Mail, Phone, Hash, Briefcase, CalendarClock, MessageSquare, Pencil } from 'lucide-react';
import { useCollaboratorById } from '../../hooks/queries/useCollaborators';
import { useLawyerWorkload } from '../../hooks/queries/useLawyerProfile';
import { useGetOrCreateDm } from '../../hooks/queries/useMessaging';
import { useSidecarStore } from '../../store/useSidecarStore';
import { useAuthStore } from '../../store/useAuthStore';
import { asCollaboratorRole, ROLE_LABELS } from '../../services/auth/permissions';
import { Avatar } from '../ui/Avatar';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';

import { formatAppDate } from '../../lib/dateFormat';
const STAGE_LABELS: Record<string, string> = {
  opened: 'Ouvert',
  pre_litigation: 'Pré-contentieux',
  investigation: 'Instruction',
  first_instance: 'Première instance',
  judgment: 'Jugement',
  appeal: 'Appel',
  enforcement: 'Exécution',
  closed: 'Terminé',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return formatAppDate(iso);
}

export function LawyerProfilePage() {
  const { collaboratorId } = useParams<{ collaboratorId: string }>();
  const navigate = useNavigate();
  const me = useAuthStore((s) => s.collaborator);
  const { data: collab, isLoading } = useCollaboratorById(collaboratorId ?? null);
  const { data: workload } = useLawyerWorkload(collaboratorId);
  const getOrCreateDm = useGetOrCreateDm();
  const selectChannel = useSidecarStore((s) => s.selectChannel);

  if (isLoading) return <div className="flex items-center justify-center py-24"><Spinner /></div>;
  if (!collab) return <div className="px-8 py-12 text-neutral-500">Collaborateur introuvable.</div>;

  const isMe = me?.id === collab.id;
  const roleLabel = ROLE_LABELS[asCollaboratorRole(collab.role) ?? 'lawyer'] ?? collab.role;

  function handleSendMessage() {
    if (!collab) return;
    getOrCreateDm.mutate(collab.id, { onSuccess: (channel) => selectChannel(channel.id) });
  }

  return (
    <div className="main-container-gdm">
    <div className="mx-auto max-w-3xl space-y-8">
      {/* En-tête */}
      <div className="bg-cime-surface rounded-cime border border-neutral-200 p-6">
        <div className="flex items-start gap-6">
          <Avatar name={collab.full_name} src={collab.avatar_url} size="2xl" className="h-20 w-20 text-2xl" />
          <div className="flex-1 min-w-0">
            <h1
              className="text-[30px] font-bold leading-tight text-cime-primary"
              style={{ fontFamily: 'var(--cime-font-body)' }}
            >
              {collab.full_name}
            </h1>
            <p className="text-sm text-neutral-500 mt-1">{roleLabel}</p>
            <div className="flex flex-wrap gap-4 mt-3 text-sm text-neutral-600">
              <span className="inline-flex items-center gap-1.5">
                <Mail className="w-4 h-4 text-neutral-400" />
                {collab.email}
              </span>
              {collab.phone && (
                <span className="inline-flex items-center gap-1.5">
                  <Phone className="w-4 h-4 text-neutral-400" />
                  {collab.phone}
                </span>
              )}
              {collab.bar_number && (
                <span className="inline-flex items-center gap-1.5">
                  <Hash className="w-4 h-4 text-neutral-400" />
                  {collab.bar_number}
                </span>
              )}
            </div>
          </div>
            <div className="flex flex-shrink-0 gap-2">
              {isMe ? (
                <Button variant="secondary" onClick={() => navigate('/profil')}>
                  <Pencil className="w-4 h-4" /> Modifier mon profil
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleSendMessage}
                  disabled={getOrCreateDm.isPending}
                >
                  <MessageSquare className="w-4 h-4" /> Envoyer un message
                </Button>
              )}
            </div>
        </div>
      </div>

      {/* Bio + compétences */}
      <div className="bg-cime-surface rounded-cime border border-neutral-200 p-6 space-y-4">
        <h2 className="text-sm font-semibold text-neutral-500 uppercase tracking-wide">À propos</h2>
        {collab.bio ? (
          <p className="text-sm text-neutral-800 whitespace-pre-wrap">{collab.bio}</p>
        ) : (
          <p className="text-sm text-neutral-400 italic">Aucune biographie renseignée.</p>
        )}
        {collab.expertise.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-2">
            {collab.expertise.map((tag) => (
              <span
                key={tag}
                className="px-2.5 py-1 rounded-full text-xs font-medium bg-cime-primary/10 text-cime-primary"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Dossiers en cours & charge */}
      <div className="bg-cime-surface rounded-cime border border-neutral-200 p-6 space-y-4">
        <h2 className="text-sm font-semibold text-neutral-500 uppercase tracking-wide">
          Dossiers en cours &amp; charge de travail
        </h2>
        {workload && !workload.visible ? (
          <p className="text-sm text-neutral-400 italic">
            Ce collaborateur ne partage pas ses dossiers en cours.
          </p>
        ) : workload ? (
          <>
            <div className="grid grid-cols-3 gap-4">
              <Stat
                icon={<Briefcase className="w-4 h-4" />}
                label="Dossiers actifs"
                value={String(workload.activeDossiersCount)}
              />
              <Stat
                icon={<CalendarClock className="w-4 h-4" />}
                label="Échéances ouvertes"
                value={String(workload.openTasksCount)}
              />
              <Stat
                icon={<CalendarClock className="w-4 h-4" />}
                label="Prochaine échéance"
                value={formatDate(workload.nextDeadline)}
              />
            </div>
            {workload.dossiers.length > 0 && (
              <ul className="divide-y divide-neutral-100 border-t border-neutral-100 pt-2">
                {workload.dossiers.map((d) => (
                  <li key={d.id}>
                    <Link
                      to={`/dossiers/${d.id}`}
                      className="flex items-center justify-between py-2.5 hover:bg-neutral-50 -mx-2 px-2 rounded"
                    >
                      <span className="min-w-0">
                        <span className="text-sm font-medium text-neutral-900">{d.number}</span>
                        <span className="text-sm text-neutral-600 ml-2 truncate">{d.title}</span>
                      </span>
                      <span className="text-xs text-neutral-400 shrink-0 ml-3">
                        {STAGE_LABELS[d.stage] ?? d.stage}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <div className="flex justify-center py-4">
            <Spinner />
          </div>
        )}
      </div>
    </div>
    </div>
  );
}

function Stat({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-cime border border-neutral-200 p-4">
      <div className="flex items-center gap-1.5 text-neutral-400">
        {icon}
        <span className="text-xs uppercase tracking-wide">{label}</span>
      </div>
      <p className="text-2xl font-bold text-cime-primary mt-1">{value}</p>
    </div>
  );
}
